import { observable, observe, action, computed, makeObservable, toJS } from 'mobx'
import { lens, just, multi } from 'lorgnette'
import * as R from 'ramda'
import { debounce, isDefined, noop, isNull } from '../../../../lib/utils/common'
import { intersection, fromServerToClient, removeFromArray, isPresent, isEmpty } from '../../../../lib/utils/collection'
import { priceLabelWithCutOff } from '../../../../lib/utils/money'
import { buildSelectedOptionsWithParents, buildTreeTownOptions } from '../../../../lib/dataMappings/treeUtils'
import { getId } from '../../../../lib/utils/selectors'
import { SRP_FORM_VALUE_CHANNEL } from '../../../../lib/constants/channels'
import {
  SRP_SEARCH_CRITERIA,
  SRP_SEARCH_VALUES_SCOPE,
  SRP_SEARCH_INFO,
  SRP_SEARCH_INFO_URL
} from '../../../../lib/constants/search'
import { toLatLngLiteral } from '../utils'
import { listingCaptchaKey } from '../../../../lib/config/recaptcha'
import { dispatchEventFetchListings, dispatchEventRemovePolygon } from '../../../Shared/stores/utils'

export const DEBOUNCE_TIMEOUT = 2000

const sharedFields = [
  'videoTour',
  'zillow3dTour',
  'townNames',
  'acreageFrom',
  'acreageTo',
  'sqftFrom',
  'sqftTo',
  'yearFrom',
  'yearTo',
  'styles',
  'openHouse',
  'byOwner',
  'byAgent',
  'newConstruction',
  'singleStory',
  'waterAccess',
  'carSpaces'
]

const bedsAndBathsFields = ['beds', 'baths']
const typeAndAmenityFields = ['unitTypes', 'amenityNames']

const commonListingFields = {
  sales: [
    ...sharedFields,
    ...bedsAndBathsFields,
    ...typeAndAmenityFields,
    'pricePerSqftFrom',
    'pricePerSqftTo'
  ],
  rentals: [
    ...sharedFields,
    ...bedsAndBathsFields,
    ...typeAndAmenityFields,
    'pricePeriods'
  ],
  lands: sharedFields
}

export const SPECIAL_AREA = 'South of Highway'
const justBoolean = R.compose(just, Boolean)

const specAreaAmenityLens = lens.prop('amenityNames').firstOf(name => name === SPECIAL_AREA)
const specAreaTownsLens = lens.prop('townNames').firstOf(name => name === SPECIAL_AREA)
const isSpecAreaInAmenities = value => (
  specAreaAmenityLens.get(value).then(justBoolean).getOr(false)
)
const isSpecAreaInTowns = value => (
  specAreaTownsLens.get(value).then(justBoolean).getOr(false)
)
const updateSpecialAreaInTowns = values => R.cond([
  [R.equals([true, false, false]),
    () => lens.prop('townNames').update(values, v => isPresent(v) ? [...v, SPECIAL_AREA] : [SPECIAL_AREA])],

  [R.equals([false, true, true]),
    () => lens.prop('townNames').update(values, v => removeFromArray(v, SPECIAL_AREA))],

  [R.T, R.always(values)]
])
const updateSpecialAreaInAmenities = values => {
  const amenityNamesLens = lens.prop('amenityNames')
  const specialAreaPropLens = lens.prop(SPECIAL_AREA, false)
  const valuesLens = multi(amenityNamesLens, specialAreaPropLens)

  const addToAmenityNames = v => isPresent(v) ? [...v, SPECIAL_AREA] : [SPECIAL_AREA]
  const enableSpecialArea = R.always(true)

  const removeFromAmenityNames = v => removeFromArray(v, SPECIAL_AREA)
  const disableSpecialArea = R.always(false)

  return R.cond([
    [R.equals([true, false, false]),
      () => valuesLens.update(values, addToAmenityNames, enableSpecialArea)],

    [R.equals([false, true, true]),
      () => valuesLens.update(values, removeFromAmenityNames, disableSpecialArea)],

    [R.T, R.always(values)]
  ])
}

const hasTownNames = newValue => lens.prop('townNames').get(newValue).then(isPresent)
const hasPolygon = newValue => lens.prop('searchPolygon').get(newValue).then(isPresent)

class ValueStateStore {
  @observable values = {}
  @observable defaultValues = {}
  @observable countResults = 0
  @observable towns = {}
  @observable mapBounds = {}
  @observable searchPolygon = []
  @observable totalPages = 0
  @observable cardPhotos = observable.map()
  @observable currentPage = 1
  @observable isTownChanged = false
  bedsValueLimits = { min: 1, max: 5 }
  bathValueLimits = { min: 1, max: 6 }

  initialValues = {}

  smokingPolicies = ['Any', 'Smoking Allowed', 'No Smoking']
  petPolicies = ['Any', 'Pets Allowed', 'Pets Considered', 'No Pets']
  possibleRuleNames = ['Pets Allowed', 'Smoking Allowed', 'No Pets', 'No Smoking', 'Pets Considered']
  amenities = ['Pool', 'Tennis', 'Sauna', 'Hot Tub', 'Central AC', 'Security System', 'Basement', 'TV/Cable/Satellite', 'Handicap Features', 'Exercise Room', 'Laundry Room', 'Fireplace', 'Garage', 'South of Highway', 'Outdoor Shower(s)', 'Water Access', 'Beach Rights', 'Dock', 'Sprinkler System', 'Pool/Guest House', 'Bonus Room', 'Breakfast Area', 'Family Room/Den', 'Great Room', 'Library', 'Loft', 'Media Room', 'Office', 'Studio', 'Study', 'Gated Community', 'Community Pool', 'Community Tennis', 'Golf Course', 'Clubhouse/Recreation', 'Docking Rights', 'Linens']
  viewAmenities = ['Oceanfront', 'Oceanview', 'Waterfront', 'Waterview', 'Bayfront', 'Bayview', 'Pondfront', 'Pondview', 'Harborfront', 'Harborview', 'Lakefront', 'Lakeview', 'Creek/Inletfront', 'Creek/Inletview', 'Canalfront', 'Canalview', 'Soundfront', 'Soundview']
  allAmenities = [...this.amenities, ...this.viewAmenities]

  optionsById = {}

  constructor({ transport, location, channel, sessionStorage, root }) {
    makeObservable(this)

    this.transport = transport
    this.location = location
    this.sessionStorage = sessionStorage
    this.root = root
    this.channel = channel
    this.channel.init(SRP_FORM_VALUE_CHANNEL)

    this.setObservers()
  }

  setInitialState({ initialState }) {
    this.setState({
      values: this.sanitizeValues(initialState.ValueStateStore.values),
      towns: initialState.ValueStateStore.towns,
      mapBounds: initialState.ValueStateStore.values.mapBounds,
      searchPolygon: initialState.ValueStateStore.values.searchPolygon,
      defaultValues: initialState.ValueStateStore.defaultValues
    })

    this.initialValues = this.values

    if (this.values && Object.keys(this.values).length > 0) {
      const searchScope = this.root.listingTypeStore.current === 'rentals' ? 'search_rental' : 'search_sale'
      this.sessionStorage.saveToSessionStorage(SRP_SEARCH_INFO, { seed: Math.floor(Math.random() * 1000000000) })
      this.sessionStorage.saveToSessionStorage(SRP_SEARCH_VALUES_SCOPE, searchScope)
      this.sessionStorage.saveToSessionStorage(SRP_SEARCH_CRITERIA, this.valuesForSubmit)
    }
  }

  setObservers() {
    observe(this, 'values', ({ newValue, oldValue }) => {
      this.values = updateSpecialAreaInTowns(this.values)([
        isSpecAreaInAmenities(newValue),
        isSpecAreaInAmenities(oldValue),
        isSpecAreaInTowns(newValue)
      ])
    })

    observe(this, 'values', ({ newValue, oldValue }) => {
      this.values = updateSpecialAreaInAmenities(this.values)([
        isSpecAreaInTowns(newValue),
        isSpecAreaInTowns(oldValue),
        isSpecAreaInAmenities(newValue)
      ])
    })
  }

  @action('[ValueStateStore] Reset state')
  setState = ({ values, towns, defaultValues }, { current } = this.root.listingTypeStore) => {
    const commonListingValues = commonListingFields[current].reduce((res, name) => {
      res[name] = values[name] || this.values[name]
      return res
    }, {})

    this.values = { ...values, ...commonListingValues }
    this.towns = towns
    this.defaultValues = defaultValues
    this.setValuesFromDefaultValues()

    this.values = {
      ...this.values,
      smokingPolicy: this.smokingPolicyValue,
      petPolicy: this.petPolicyValue,
      ...this.allAmenityNames,
      ruleNames: this.collectRuleNames({ ...this.values, smokingPolicy: this.smokingPolicyValue, petPolicy: this.petPolicyValue })
    }

    this.channel.put(SRP_FORM_VALUE_CHANNEL, { value: this.values })
  }

  @action('[ValueStateStore] Update input value')
  setInputValue = (name, value) => {
    const newValues = {
      ...this.values,
      [name]: value
    }

    if (name === 'beds' || name === 'baths') {
      newValues[name] = Number(value)
    }

    this.values = {
      ...newValues,
      ruleNames: this.collectRuleNames(newValues),
      amenityNames: this.allAmenities.filter(n => newValues[n])
    }

    this.channel.put(SRP_FORM_VALUE_CHANNEL, { value: this.values })
  }

  @action('[ValueStateStore] Reset to initial values')
  resetToInitialValues = () => {
    this.values = this.initialValues
    this.setValuesFromDefaultValues()
  }

  @action('[ValueStateStore] Reset inputs')
  resetInputs = names => {
    this.values = { ...R.omit(names, this.values) }
  }

  @action('[ValueStateStore] Set flag if town changed')
  setTownChanged = value => {
    this.isTownChanged = value
  }

  @action('[ValueStateStore] Reset inputs')
  resetInputToInitialValue = name => {
    this.values = { ...this.values, [name]: this.initialValues[name] }
  }

  @action('[ValueStateStore] Reset all inputs')
  resetAllInputs = () => {
    this.values = {}

    this.setState({
      values: {},
      towns: this.towns,
      defaultValues: this.defaultValues
    })
  }

  @action('[ValueStateStore] Remove polygon')
  removePolygon = () => {
    this.setInputValue('searchPolygon', [])
    this.searchPolygon = []
    dispatchEventRemovePolygon()
  }

  @action('[ValueStateStore] Clear map bounds')
  clearMapBounds = () => {
    this.setInputValue('mapBounds', {})
    this.mapBounds = {}
  }

  @action('[ValueStateStore] Handle reset filters')
  handleResetFilters = () => {
    this.resetAllInputs()
    this.getSearchInfoDebounced()
    return true
  }

  @action('[ValueStateStore] Preset defaults')
  setValuesFromDefaultValues = () => {
    this.values = {
      ...this.values,
      year: this.values.year || this.defaultValues.year,
      unitTypes: this.values.unitTypes || this.defaultValues.unitTypes,
      status: this.values.status || this.defaultValues.status,
      pricePeriods: this.values.pricePeriods || this.defaultValues.pricePeriods || []
    }
  }

  @action('[ValueStateStore] Save searchScope to sessionStorage')
  saveSearchScope = (current) => {
    const valuesScope = current === 'rentals' ? 'search_rental' : 'search_sale'
    this.sessionStorage.saveToSessionStorage(SRP_SEARCH_VALUES_SCOPE, valuesScope)
  }

  @action('[ValueStateStore] Reset to default values')
  resetToDefaultValues = () => {
    this.values = {
      ...this.values,
      status: this.defaultValues.status,
      unitTypes: this.defaultValues.unitTypes,
      year: this.defaultValues.year,
      pricePeriods: this.defaultValues.pricePeriods || []
    }
  }

  @action('[ValueStateStore] Submit form')
  submit = ({ current, listingTypes }) => {
    const valuesScope = current === 'rentals' ? 'search_rental' : 'search_sale'

    this.transport.Search
      .submit(listingTypes[current].searchUrl, { [valuesScope]: this.valuesForSubmit, page: this.currentPage })
      .then(({ redirect_to }) => {
        this.location.redirect(redirect_to)
      })
      .catch(noop)
  }

  @action('[ValueStateStore] get count results')
  getSearchInfo = ({ current, listingTypes }, { saveAsInitial = false, omitMapBounds = true } = {}) => {
    if (isNull(current)) {
      return
    }

    let newValues = R.clone(this.valuesForSubmit);
    if (omitMapBounds) {
      newValues = R.omit(['mapBounds'], newValues)
    }

    const valuesScope = current === 'rentals' ? 'search_rental' : 'search_sale'

    this.transport.Search
      .getSearchInfo(listingTypes[current].countResultsUrl, { [valuesScope]: newValues, page: this.currentPage })
      .then(response => {
        const { totalCount, currentPage, totalPages } = fromServerToClient(response)

        this.setCountResults(totalCount)
        this.setCurrentPage(currentPage)
        this.setTotalPages(totalPages)

        if (saveAsInitial) {
          this.initialValues = { ...this.initialValues, countResults: totalCount, currentPage, totalPages }
        }
      })
      .catch(noop)
  }

  getSearchInfoDebounced = debounce(({ omitMapBounds = true } = {}) => {
    this.getSearchInfo(this.root.listingTypeStore, { omitMapBounds })
  }, 500)

  @action('[ValueStateStore] fetch new data for map and listings')
  fetchDataForListings = v => {
    this.resetCurrentPage()
    dispatchEventFetchListings()
  }

  @action('[ValueStateStore] set count results')
  setCountResults = v => {
    this.countResults = v
  }

  @action('[ValueStateStore] paginate')
  paginate = page => {
    const topElement = document.querySelector('main')
    if (topElement) {
      topElement.scrollIntoView({ behavior: 'smooth', block: 'start', inline: 'nearest' })
    }

    this.setCurrentPage(page)
    dispatchEventFetchListings()
  }

  @computed
  get valuesForSubmit() {
    const updateStatus = status => {
      const s = isDefined(status) ? status : []
      return s.length === 0 ? ['any'] : s
    }
    const statusLens = lens.prop('status')

    const updateTownNames = townNames => (
      isDefined(townNames)
        ? removeFromArray(townNames, SPECIAL_AREA)
        : townNames
    )
    const townNamesLens = lens.prop('townNames')

    const valuesLens = multi(statusLens, townNamesLens)

    let newValues = valuesLens.update(this.values, updateStatus, updateTownNames)

    if (!newValues.townNames || newValues.townNames.length === 0) {
      newValues = R.omit(['fullTownNames'], newValues)
    }

    const hasSpecialAreaAmenity = this.values.amenityNames &&
      this.values.amenityNames.includes(SPECIAL_AREA)

    if (hasTownNames(newValues)) {
      newValues = R.omit(['mapBounds', 'searchPolygon'], newValues)
    }

    if (hasSpecialAreaAmenity) {
      newValues = R.omit(['mapBounds', 'searchPolygon'], newValues)
    }

    if (Array.isArray(newValues.searchPolygon) && newValues.searchPolygon.length > 0) {
      newValues = R.omit(['mapBounds'], newValues)
    }

    const fieldsToOmitIfFalsy = [
      'beds', 'baths', 'sqftFrom', 'sqftTo',
      'acreageFrom', 'acreageTo', 'yearFrom', 'yearTo', 'openHouse'
    ]

    const fieldsToOmitIfEmpty = ['amenityNames', 'styles', 'pricePeriods', 'ruleNames']

    fieldsToOmitIfFalsy.forEach(field => {
      if (!newValues[field]) {
        newValues = R.omit([field], newValues)
      }
    })

    fieldsToOmitIfEmpty.forEach(field => {
      if (!newValues[field] || newValues[field].length === 0) {
        newValues = R.omit([field], newValues)
      }
    })

    if (newValues.carSpaces) {
      if (newValues.carSpaces === 'Any') {
        newValues = R.omit(['carSpaces'], newValues)
      } else {
        const normalized = newValues.carSpaces.toString().replace('+', '')
        const num = parseInt(normalized, 10)
        if (num && !isNaN(num)) {
          newValues.carSpaces = normalized
        } else {
          newValues = R.omit(['carSpaces'], newValues)
        }
      }
    }

    return newValues
  }

  saveSearchInfo() {
    const { current, listingTypes } = this.root.listingTypeStore
    const valuesScope = current === 'rentals' ? 'search_rental' : 'search_sale'

    this.sessionStorage.saveToSessionStorage(SRP_SEARCH_VALUES_SCOPE, valuesScope)
    this.sessionStorage.saveToSessionStorage(SRP_SEARCH_CRITERIA, this.valuesForSubmit)
    this.sessionStorage.saveToSessionStorage(SRP_SEARCH_INFO_URL, listingTypes[current].countResultsUrl)

    const page = (new URL(this.location.global.href)).searchParams.get('page') || 1

    this.setCurrentPage(page)

    this.transport.Search
      .getSearchInfo(listingTypes[current].countResultsUrl, { [valuesScope]: this.valuesForSubmit, page: this.currentPage })
      .then(data => {
        const searchInfo = fromServerToClient(data)
        const savedSearchInfo = this.sessionStorage.fetchFromSessionStorage(SRP_SEARCH_INFO)
        const savedSearchItems = lens.prop('items').get(savedSearchInfo).getOr({})
        const savedSearchId = lens.prop('searchId').get(savedSearchInfo).getOr(null)

        const newSearchInfo = lens.prop('items')
          .update(searchInfo, items => (
            savedSearchId === searchInfo.searchId
              ? { ...savedSearchItems, [this.currentPage]: items }
              : { [this.currentPage]: items }
          ))

        this.sessionStorage.saveToSessionStorage(SRP_SEARCH_INFO, newSearchInfo)
      })
      .catch(noop)
  }

  sanitizeValues = values => ({
    ...values,
    status: values.status && values.status.filter(v => v !== 'any')
  })

  debounceSubmitFunction = debounce(values => { this.submit(values) }, DEBOUNCE_TIMEOUT)

  debounceSubmitCancel = () => {
    clearTimeout(this.debounceSubmitFunction.timeout)
  }

  @action('[ValueStateStore] Delayed form submit')
  debounceSubmit = this.debounceSubmitFunction

  @action('[ValueStateStore] Immediate form submit with planned attempt cancelled')
  immediateSubmit = values => {
    this.debounceSubmitCancel()
    this.submit(values)
  }

  @action('[ValueStateStore] Set Search polygon')
  setSearchPolygon = polygon => {
    const literalCollection = toLatLngLiteral(polygon)
    this.searchPolygon = literalCollection
    this.setInputValue('searchPolygon', literalCollection)
  }

  @action('[ValueStateStore] Reset current page')
  resetCurrentPage = () => {
    this.setCurrentPage(1)
  }

  @action('[ValueStateStore] Map submit when drawing polygon area')
  mapPolygonSubmit = () => {
    this.setInputValue('townNames', [])
    this.setInputValue('mapBounds', {})

    const polygon = !this.searchPolygon?.length ? this.values.searchPolygon : this.searchPolygon
    this.setInputValue('searchPolygon', polygon)

    this.fetchDataForListings()

    this.getSearchInfoDebounced()
  }

  @action('[ValueStateStore] Map submit when drag or zoom')
  mapChangeSubmit = () => {
    if (this.searchPolygon.length > 0) {
      return
    }
    this.setInputValue('townNames', [])
    this.setInputValue('mapBounds', this.mapBounds)

    this.fetchDataForListings()

    this.getSearchInfoDebounced({ omitMapBounds: false })
  }

  @action('[ValueStateStore] Immediate mapSearch form submit with planned attempt cancelled')
  mapSearchSubmit = values => {
    this.setInputValue('townNames', [])
    this.setInputValue('mapBounds', this.mapBounds)
    this.setInputValue('searchPolygon', this.searchPolygon)
    this.immediateSubmit(values)
  }

  @action('[ValueStateStore] set current page')
  setCurrentPage = v => {
    this.currentPage = v
  }

  @action('[ValueStateStore] set total pages')
  setTotalPages = v => {
    this.totalPages = v
  }

  @action('[ValueStateStore] get card photos')
  getCardPhotos = async (listingType, listingId, filters) => {
    try {
      const response = await this.transport.Listing
        .getCardPhotos(listingType, listingId, filters)
      return response.photos
    } catch (e) {
      noop()
    }
  }


  getAllDescendantOptions(option, accumulator = []) {
    const children = (option.children || [])
      .map(childId => this.optionsById[childId])
      .filter(child => child && getId(child) !== SPECIAL_AREA)

    children.forEach(child => {
      accumulator.push(child)
      this.getAllDescendantOptions(child, accumulator)
    })

    return accumulator
  }

  @computed
  get selectedOptionsGroup() {
    const townNameIds = this.values.townNames || []
    const allSelectedOptions = []
    const addedIds = new Set()

    const addOption = (option) => {
      const optionId = getId(option)
      if (!addedIds.has(optionId)) {
        allSelectedOptions.push(option)
        addedIds.add(optionId)
      }
    }

    townNameIds.forEach(id => {
      const option = this.optionsById[id]
      if (!option) return

      addOption(option)
      this.getAllDescendantOptions(option).forEach(addOption)
    })

    return allSelectedOptions
  }

  @computed
  get primarySelectedOptions() {
    const selectedIds = this.selectedOptionsGroup.map(option => getId(option))
    return this.treeTownOptions.filter(o => (
      selectedIds.includes(getId(o))
    ))
  }

  @computed
  get selectedOptionsWithParents() {
    return buildSelectedOptionsWithParents(this.treeTownOptions, this.primarySelectedOptions, this.optionsById)
  }

  collectRuleNames(values) {
    const checked = this.possibleRuleNames.filter(ruleName => values[ruleName] === true)
    const legacy = [values.smokingPolicy, values.petPolicy].filter(n => isDefined(n) && n !== 'Any')
    return [...new Set([...checked, ...legacy])]
  }

  @computed
  get ruleNames() {
    return (isDefined(this.values.ruleNames) && Array.isArray(this.values.ruleNames))
      ? this.values.ruleNames
      : this.collectRuleNames(this.values)
  }

  @computed
  get allAmenityNames() {
    return this.allAmenities.reduce((acc, cur) => {
      acc[cur] = isDefined(this.values.amenityNames) && this.values.amenityNames.includes(cur)
      return acc
    }, {})
  }

  @computed
  get priceValues() {
    const { priceFrom: defaultPriceFrom, priceTo: defaultPriceTo } = this.defaultValues
    const { priceFrom = defaultPriceFrom, priceTo = defaultPriceTo } = this.values

    return [priceFrom, priceTo]
  }

  priceTitle(cutOff) {
    const { priceFrom, priceTo } = this.values

    if (isDefined(priceFrom) || isDefined(priceTo)) {
      const { priceFrom: defaultPriceFrom, priceTo: defaultPriceTo } = this.defaultValues

      return (
        [priceFrom || defaultPriceFrom, priceTo || defaultPriceTo].map(v => priceLabelWithCutOff(v, cutOff)).join(' - ')
      )
    }

    return 'Price'
  }

  @computed
  get bedsValue() {
    const { beds: defaultBeds } = this.defaultValues
    const { beds = defaultBeds } = this.values

    return beds > this.bedsValueLimits.max ? this.bedsValueLimits.max : beds
  }

  @computed
  get bathsValue() {
    const { baths: defaultBaths } = this.defaultValues
    const { baths = defaultBaths } = this.values

    return baths > this.bathValueLimits.max ? this.bathValueLimits.max : baths
  }

  @computed
  get bedsBathsTitle() {
    const { beds, baths } = this.values

    if (isDefined(beds) || isDefined(baths)) {
      const bedsValue = beds ? `${beds}+` : 'Any'
      const bathsValue = baths ? `${baths}+` : 'Any'

      return `${bedsValue} BD, ${bathsValue} BA`
    }

    return 'Beds + Baths'
  }

  @computed
  get bedsTitle() {
    const { beds } = this.values
    return isDefined(beds) ? `${beds} bedrooms` : null
  }

  @computed
  get acreageValue() {
    const { acreage: defaultAcreage } = this.defaultValues
    const { acreage = defaultAcreage } = this.values

    return acreage
  }

  @computed
  get sqftValue() {
    const { sqft: defaultSqft } = this.defaultValues
    const { sqft = defaultSqft } = this.values

    return sqft
  }

  @computed
  get smokingPolicyValue() {
    const { ruleNames = [] } = this.values
    return intersection(this.smokingPolicies, ruleNames)[0] || this.smokingPolicies[0]
  }

  @computed
  get petPolicyValue() {
    const { ruleNames = [] } = this.values
    return intersection(this.petPolicies, ruleNames)[0] || this.petPolicies[0]
  }

  @computed
  get viewAmenityValue() {
    const { amenityNames = [] } = this.values
    return intersection(this.viewAmenities, amenityNames) || []
  }

  @computed
  get treeTownOptions() {
    const withSpecialArea = [
      { id: SPECIAL_AREA, name: SPECIAL_AREA, children: [], parent: { id: 'The Hamptons' } },
      ...(R.sortBy(R.prop('id'), this.towns.collection))
    ]
    const options = buildTreeTownOptions(withSpecialArea)

    this.optionsById = R.indexBy(R.prop('id'), options)
    return options
  }
}

export default ValueStateStore
